import { StandardEvents } from '@shopify/events';

/**
 * Re-renders the product specifications table on variant change.
 *
 * Horizon's variant picker dispatches a ProductSelectEvent (bubbles to document) whose
 * `promise` resolves with the full product page fetched with `?option_values=...`.
 * The Liquid in sections/product-specs.liquid renders variant-specific rows from
 * `product.selected_or_first_available_variant`, so the table in that HTML is exactly
 * what the new variant needs; we swap it in without any client-side data model.
 */
class TdpProductSpecs extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @param {Event & { promise?: Promise<{ detail?: { html?: Document, productId?: string } }> }} event */
  #handleProductSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('product-card, quick-add-dialog')) return;
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;
        if (detail.productId && this.dataset.productId && detail.productId !== this.dataset.productId) return;

        const sectionId = this.dataset.sectionId;
        const next = html.querySelector(`[data-tdp-specs-table="${sectionId}"]`);
        const current = this.querySelector(`[data-tdp-specs-table="${sectionId}"]`);
        if (!next || !current) return;

        current.replaceWith(next.cloneNode(true));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[tdp-product-specs] variant update failed:', error);
      });
  };
}

if (!customElements.get('tdp-product-specs')) {
  customElements.define('tdp-product-specs', TdpProductSpecs);
}
